type SrcdocExtensionScript = {
  name: string;
  scriptUrl: string;
};

type CreateSrcdocOptions = {
  frameId: string;
  html: string;
  extensions: readonly SrcdocExtensionScript[];
  // Web/archive/portal renders ship `/static/...` references that point at
  // production assets; under the dev-server origin those 404. Opt-in rewrite
  // pins them to buttondown.com so previews work locally too.
  rewriteStaticToProd?: boolean;
};

const esc = (s: string) => {
  return s
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
};

// In dev, Vite serves extension scripts off its own origin via
// `?worker&url`; in prod the bundled URL is root-relative and needs to be
// resolved against the parent origin so the iframe (whose origin is the
// blob: URL) can fetch them.
const resolveScriptUrl = (scriptUrl: string) => {
  return import.meta.env.PROD
    ? new URL(scriptUrl, location.origin).href
    : scriptUrl;
};

export const createSrcdoc = ({
  frameId,
  html: rawHtml,
  extensions,
  rewriteStaticToProd = false,
}: CreateSrcdocOptions): string => {
  const html = rewriteStaticToProd
    ? rawHtml.replaceAll('href="/static', 'href="https://buttondown.com/static')
    : rawHtml;

  // Opens iframe links in a new tab and lets relative URLs (e.g. /static/...
  // in non-email contexts) resolve against the parent origin in dev.
  const baseTag = `<base target="_blank" href="${esc(location.origin)}">`;

  const extensionScripts = extensions
    .map((ext) => {
      return `<script type="module" src="${esc(
        resolveScriptUrl(ext.scriptUrl)
      )}"></script>`;
    })
    .join("\n");

  const headInjection = `${baseTag}${extensionScripts}`;

  const frameIdAttr = ` data-frame-id="${esc(frameId)}"`;

  // Stamp data-frame-id onto the existing <html> open tag so iframe.ts can
  // read it from `document.documentElement.dataset.frameId`. Module scripts
  // (and async/deferred classics) get `document.currentScript` === null, so
  // the script tag itself isn't a viable carrier.
  const htmlOpen = html.match(/<html\b[^>]*>/i);
  const headClose = html.match(/<\/head\s*>/i);

  if (htmlOpen?.index !== undefined && headClose?.index !== undefined) {
    const stampedHtmlOpen = htmlOpen[0].replace(/>$/, `${frameIdAttr}>`);
    const before = html.slice(0, htmlOpen.index);
    const between = html.slice(
      htmlOpen.index + htmlOpen[0].length,
      headClose.index
    );
    const after = html.slice(headClose.index);
    // Force standards mode. Without a doctype, quirks-mode rules promote
    // <body> to the scrolling element, which breaks scroll-sync (which
    // reads/writes documentElement.scrollTop).
    const doctype = /<!DOCTYPE/i.test(before) ? "" : "<!DOCTYPE html>";
    return `${doctype}${before}${stampedHtmlOpen}${between}${headInjection}${after}`;
  }

  // Fallback: rendered HTML wasn't a full document. Wrap minimally so our
  // scripts still execute and the frameId still lands on <html>.
  return `<!DOCTYPE html>
<html${frameIdAttr}>
<head>${headInjection}</head>
${html}
</html>`;
};
